// StatTile — a KPI cell: label · big value · optional trend delta + sparkline.
// The value stays proportional figures (crisp at display size); columns
// elsewhere use tabular-nums. `accent` colors the top border + icon chip and
// takes any token. Optional `onClick` makes it an interactive card.
//
//   <StatTile label="Active employees" value={42} icon={<Icon name="users" />} onClick={…} />
//   <StatTile
//     label="Placements"
//     value={128}
//     accent="var(--good)"
//     delta={{ value: '+12%', direction: 'up' }}
//     trend={[90,96,104,112,120,128]}
//   />

import { ReactNode } from 'react';
import Sparkline from './charts/Sparkline';
import Icon from './Icon';

export interface StatDelta {
  value: ReactNode;
  direction?: 'up' | 'down' | 'flat';
}

export interface StatTileProps {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  /** Token color for the accent border + icon chip. Default brand. */
  accent?: string;
  delta?: StatDelta;
  /** Sparkline series (drawn in `accent`). */
  trend?: number[];
  onClick?: () => void;
  title?: string;
  className?: string;
}

export default function StatTile({ label, value, sub, icon, accent, delta, trend, onClick, title, className }: StatTileProps) {
  const interactive = !!onClick;
  const cls = [
    'ui-stat',
    accent ? 'ui-stat--accent' : '',
    interactive ? 'ui-stat--interactive' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  const dirClass = delta?.direction === 'down' ? 'ui-stat__delta--down' : delta?.direction === 'flat' ? 'ui-stat__delta--flat' : 'ui-stat__delta--up';

  return (
    <div
      className={cls}
      style={accent ? ({ ['--stat-accent' as string]: accent } as React.CSSProperties) : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick!(); } } : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      title={title}
    >
      <div className="ui-stat__top">
        <span className="ui-stat__label">{label}</span>
        {icon && (
          <span className="ui-stat__icon" style={accent ? { background: 'color-mix(in srgb, var(--stat-accent) 14%, transparent)', color: 'var(--stat-accent)' } : undefined} aria-hidden>
            {icon}
          </span>
        )}
      </div>
      <div className="ui-stat__value">{value}</div>
      {(sub || delta) && (
        <div className="ui-stat__row">
          {sub && <span className="ui-stat__sub">{sub}</span>}
          {delta && (
            <span className={`ui-stat__delta ${dirClass}`}>
              {delta.direction !== 'flat' && <Icon name={delta.direction === 'down' ? 'trending-down' : 'trending-up'} size={12} />} {delta.value}
            </span>
          )}
        </div>
      )}
      {trend && trend.length > 1 && (
        <div className="ui-stat__spark">
          <Sparkline data={trend} color={accent ?? 'var(--chart-1)'} width={240} height={34} fluid ariaLabel="Trend" />
        </div>
      )}
    </div>
  );
}
